import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import { ArrowLeftOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { tablePagination, useServerPagination } from '../../components/ui'
import {
  joinList,
  splitList,
  type DocLocateFieldDef,
  type DocLocateImportResult,
  type DocLocateTemplate,
} from './docLocateShared'

export function DocLocateTemplateFields() {
  const { templateId: templateIdParam } = useParams()
  const templateId = Number(templateIdParam)
  const navigate = useNavigate()

  const [template, setTemplate] = useState<DocLocateTemplate | null>(null)
  const [tplLoading, setTplLoading] = useState(true)
  const [tplOpen, setTplOpen] = useState(false)
  const [tplForm] = Form.useForm()
  const [tplSaving, setTplSaving] = useState(false)

  const [rows, setRows] = useState<DocLocateFieldDef[]>([])
  const [total, setTotal] = useState(0)
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [loading, setLoading] = useState(false)
  const [fieldOpen, setFieldOpen] = useState(false)
  const [fieldEditing, setFieldEditing] = useState<DocLocateFieldDef | null>(null)
  const [fieldForm] = Form.useForm()
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<DocLocateImportResult | null>(null)

  const loadTemplate = useCallback(() => {
    if (!Number.isFinite(templateId) || templateId <= 0) return Promise.resolve()
    setTplLoading(true)
    return adminApi
      .get<DocLocateTemplate>(`/admin/bid-parse/templates/${templateId}`)
      .then((r) => setTemplate(r.data))
      .catch((e) => {
        message.error(e.message)
        navigate('/manage/doc-locate', { replace: true })
      })
      .finally(() => setTplLoading(false))
  }, [templateId, navigate])

  const loadFields = useCallback(() => {
    if (!Number.isFinite(templateId) || templateId <= 0) return
    setLoading(true)
    adminApi
      .get<{ items: DocLocateFieldDef[]; total: number }>(
        `/admin/bid-parse/templates/${templateId}/fields/manage`,
        { params: { page, page_size: pageSize } },
      )
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [templateId, page, pageSize])

  useEffect(() => {
    void loadTemplate()
  }, [loadTemplate])

  useEffect(() => {
    loadFields()
  }, [loadFields])

  const importFieldsToTemplate = (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return adminApi.post<DocLocateImportResult>(`/admin/bid-parse/templates/${templateId}/fields/import`, fd)
  }

  const openEditTemplate = () => {
    if (!template) return
    tplForm.setFieldsValue({
      template_name: template.template_name,
      description: template.description,
      sort_no: template.sort_no,
      is_active: template.is_active,
    })
    setTplOpen(true)
  }

  const saveTemplate = () => {
    void tplForm.validateFields().then((v) => {
      setTplSaving(true)
      adminApi
        .put(`/admin/bid-parse/templates/${templateId}`, {
          template_name: v.template_name,
          description: v.description,
          sort_no: v.sort_no ?? 0,
          is_active: v.is_active ?? true,
        })
        .then(() => {
          message.success('模板已保存')
          setTplOpen(false)
          void loadTemplate()
        })
        .catch((e) => message.error(e.message))
        .finally(() => setTplSaving(false))
    })
  }

  const deleteTemplate = () => {
    adminApi
      .delete(`/admin/bid-parse/templates/${templateId}`)
      .then(() => {
        message.success('模板已删除')
        navigate('/manage/doc-locate')
      })
      .catch((e) => message.error(e.message))
  }

  const openCreateField = () => {
    setFieldEditing(null)
    fieldForm.resetFields()
    fieldForm.setFieldsValue({ sort_no: 0, is_active: true })
    setFieldOpen(true)
  }

  const openEditField = (row: DocLocateFieldDef) => {
    setFieldEditing(row)
    fieldForm.setFieldsValue({
      field_code: row.field_code,
      field_name: row.field_name,
      semantic_queries: joinList(row.semantic_queries_json),
      keywords: joinList(row.keywords_json),
      preferred_sections: joinList(row.preferred_sections_json),
      sort_no: row.sort_no,
      is_active: row.is_active,
    })
    setFieldOpen(true)
  }

  const saveField = () => {
    void fieldForm.validateFields().then((v) => {
      const shared = {
        field_name: v.field_name,
        semantic_queries: splitList(v.semantic_queries),
        keywords: splitList(v.keywords),
        preferred_sections: splitList(v.preferred_sections),
        sort_no: v.sort_no ?? 0,
        is_active: v.is_active ?? true,
      }
      const req = fieldEditing
        ? adminApi.put(`/admin/bid-parse/templates/${templateId}/fields/${fieldEditing.id}`, shared)
        : adminApi.post(`/admin/bid-parse/templates/${templateId}/fields`, {
            field_code: v.field_code,
            ...shared,
          })
      return req
        .then(() => {
          message.success('已保存')
          setFieldOpen(false)
          loadFields()
          void loadTemplate()
        })
        .catch((e) => message.error(e.message))
    })
  }

  const downloadTemplate = () => {
    if (!template) return
    adminApi
      .get(`/admin/bid-parse/templates/${templateId}/fields/template`, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data as Blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${template.template_name}定位字段.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((e) => message.error(e.message))
  }

  const uploadExcel = (file: File) => {
    setImporting(true)
    importFieldsToTemplate(file)
      .then((r) => {
        setImportResult(r.data)
        loadFields()
        void loadTemplate()
        const { created, updated, skipped } = r.data
        message.success(`导入完成：新增 ${created}，更新 ${updated}${skipped ? `，跳过 ${skipped}` : ''}`)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setImporting(false))
    return false
  }

  if (!Number.isFinite(templateId) || templateId <= 0) {
    return null
  }

  return (
    <>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0, marginBottom: 8 }}
        onClick={() => navigate('/manage/doc-locate')}
      >
        返回模板列表
      </Button>

      <Card loading={tplLoading}>
        <Space wrap style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
          <Space wrap align="start">
            <Typography.Title level={5} style={{ margin: 0 }}>
              {template?.template_name || '—'}
              {template?.is_builtin ? (
                <Tag style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                  默认
                </Tag>
              ) : null}
            </Typography.Title>
            {template?.description ? (
              <Typography.Text type="secondary">{template.description}</Typography.Text>
            ) : null}
          </Space>
          <Space wrap>
            <Button onClick={openEditTemplate} disabled={!template}>
              编辑模板
            </Button>
            <Popconfirm
              title={
                template?.is_builtin
                  ? '删除默认模板？关联的历史定位任务将一并删除；下次使用时会自动重建空默认模板。'
                  : '确定删除该模板及全部定位字段？关联的历史定位任务将一并删除。'
              }
              onConfirm={deleteTemplate}
              disabled={!template}
            >
              <Button danger disabled={!template}>
                删除模板
              </Button>
            </Popconfirm>
          </Space>
        </Space>

        <Card
          size="small"
          title={`定位字段（${template?.field_count ?? total}）`}
          extra={
            <Space wrap>
              <Button icon={<DownloadOutlined />} disabled={!template} onClick={downloadTemplate}>
                下载 Excel
              </Button>
              <Upload accept=".xlsx" showUploadList={false} beforeUpload={uploadExcel}>
                <Button icon={<UploadOutlined />} loading={importing} disabled={!template}>
                  导入 Excel
                </Button>
              </Upload>
              <Button type="primary" icon={<PlusOutlined />} disabled={!template} onClick={openCreateField}>
                新增字段
              </Button>
            </Space>
          }
        >
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={rows}
            pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
            columns={[
              { title: '编码', dataIndex: 'field_code', width: 140, ellipsis: true },
              { title: '名称', dataIndex: 'field_name', ellipsis: true },
              {
                title: '语义问句',
                dataIndex: 'semantic_queries_json',
                ellipsis: true,
                render: (v: string[] | null) => joinList(v) || '—',
              },
              {
                title: '关键词',
                dataIndex: 'keywords_json',
                width: 160,
                ellipsis: true,
                render: (v: string[] | null) => joinList(v) || '—',
              },
              { title: '排序', dataIndex: 'sort_no', width: 64 },
              {
                title: '状态',
                dataIndex: 'is_active',
                width: 72,
                render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
              },
              {
                title: '操作',
                width: 120,
                render: (_: unknown, r: DocLocateFieldDef) => (
                  <Space size="small">
                    <Button size="small" onClick={() => openEditField(r)}>
                      编辑
                    </Button>
                    <Popconfirm
                      title="确定删除该字段？"
                      onConfirm={() =>
                        adminApi
                          .delete(`/admin/bid-parse/templates/${templateId}/fields/${r.id}`)
                          .then(() => {
                            message.success('已删除')
                            loadFields()
                            void loadTemplate()
                          })
                          .catch((e) => message.error(e.message))
                      }
                    >
                      <Button size="small" danger>
                        删
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Card>

      <Modal
        title="编辑模板"
        open={tplOpen}
        onOk={saveTemplate}
        onCancel={() => setTplOpen(false)}
        confirmLoading={tplSaving}
        destroyOnHidden
        width={520}
      >
        <Form form={tplForm} layout="vertical">
          <Form.Item
            name="template_name"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="如 项目合同、采购文件" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} placeholder="适用场景说明（可选）" />
          </Form.Item>
          <Form.Item name="sort_no" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={fieldEditing ? '编辑字段' : '新增字段'}
        open={fieldOpen}
        onOk={saveField}
        onCancel={() => setFieldOpen(false)}
        destroyOnHidden
        width={560}
      >
        <Form form={fieldForm} layout="vertical">
          <Form.Item name="field_code" label="编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input placeholder="如 project_deadline" disabled={!!fieldEditing} />
          </Form.Item>
          <Form.Item name="field_name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如 截止时间" />
          </Form.Item>
          <Form.Item name="semantic_queries" label="语义检索问句（逗号分隔）">
            <Input.TextArea rows={2} placeholder="本项目的截止时间是什么时候？" />
          </Form.Item>
          <Form.Item name="keywords" label="关键词（逗号分隔）">
            <Input placeholder="截止,递交" />
          </Form.Item>
          <Form.Item name="preferred_sections" label="优先章节（逗号分隔）">
            <Input placeholder="总则,说明" />
          </Form.Item>
          <Form.Item name="sort_no" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="导入结果"
        open={!!importResult}
        onCancel={() => setImportResult(null)}
        footer={
          <Button type="primary" onClick={() => setImportResult(null)}>
            知道了
          </Button>
        }
        width={520}
      >
        {importResult ? (
          <>
            <Typography.Paragraph>
              共解析 {importResult.total_rows} 行：新增 {importResult.created}，更新{' '}
              {importResult.updated}
              {importResult.skipped ? `，跳过 ${importResult.skipped}` : ''}
            </Typography.Paragraph>
            {importResult.errors.length ? (
              <>
                <Typography.Text type="secondary">以下行未导入：</Typography.Text>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, maxHeight: 240, overflow: 'auto' }}>
                  {importResult.errors.map((e) => (
                    <li key={`${e.row}-${e.message}`}>
                      第 {e.row} 行：{e.message}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}
      </Modal>
    </>
  )
}
